import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { PasswordInput } from 'components/ui/password-input';
import { TermsCheckbox } from 'components/auth/TermsCheckbox';
import { auth } from 'services/api';
import { useAuth } from 'contexts/AuthContext';
import { GoogleButton } from 'components/google-button';
import { API_BASE_URL } from 'config/apiConfig';
import { Icon } from 'components/icons/Icon';
import { ButtonLink } from 'components/ui/button-link';
import { Separator } from 'components/ui/separator';
import { normalizeAuthenticatedUser } from 'utils/authUser';
import { validatePasswordStrength } from 'utils/passwordValidation';
import { isAuthRevocationSafetyError } from 'utils/authTransitions';

import { isNativeMobile } from 'mobile/nativeBridge';
import { signInWithNativeGoogle } from 'mobile/nativeOAuth';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const registerNavigation = {
  redirect(url: string) {
    window.location.assign(url);
  },
};

const Register: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupCode, setSignupCode] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const nativeGoogleAbort = useRef<AbortController | null>(null);
  const [nativeGooglePending, setNativeGooglePending] = useState(false);
  useEffect(() => () => nativeGoogleAbort.current?.abort(), []);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  const trimmedEmailValue = email.trim();

  const passwordValidation = useMemo(
    () => validatePasswordStrength(password),
    [password],
  );
  const passwordValidationMessage = passwordValidation.message;

  const hasRequiredFields = Boolean(
    signupCode.trim() &&
      firstName.trim() &&
      lastName.trim() &&
      trimmedEmailValue &&
      password &&
      confirmPassword
  );
  const isEmailValid = EMAIL_REGEX.test(trimmedEmailValue);
  const doPasswordsMatch = password === confirmPassword;
  const isSubmitDisabled =
    nativeGooglePending || !hasRequiredFields ||
    !isEmailValid ||
    !doPasswordsMatch ||
    !agreedToTerms ||
    Boolean(passwordValidationMessage);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');

    if (!authError) {
      return;
    }

    const errorMessages: Record<string, string> = {
      signup_code_required: 'A signup code is required to sign up with Google.',
      signup_code_invalid: 'The signup code is invalid. Please check it and try again.',
      signup_code_inactive: 'This signup code is inactive. Contact your administrator.',
      signup_code_expired: 'This signup code has expired. Contact your administrator for a new code.',
      signup_code_exhausted: 'This signup code can no longer be used.',
      email_domain_not_allowed: 'Your email domain is not allowed for this signup code.',
      organization_invalid: 'The organization linked to this signup code is inactive or invalid.',
      oauth_denied: 'Google sign up was cancelled. Please try again.',
      no_code: 'Google sign up failed to complete. Please try again.',
      invalid_state: 'Google sign up session expired. Please try again.',
      oauth_error: 'Google sign up failed. Please try again.',
      server_error: 'Unexpected error completing Google sign up. Please try again.',
      redirect_error: 'We were unable to finish Google sign up. Please try again.',
    };

    setError(errorMessages[authError] ?? 'Google sign up failed. Please try again.');
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);

    if (!agreedToTerms) {
      setError('You must agree to the Terms and Conditions');
      return;
    }

    if (passwordValidationMessage) {
      return;
    }

    if (!doPasswordsMatch) {
      setError('Passwords do not match');
      return;
    }

    if (!isEmailValid) {
      setError('Please enter a valid email address');
      return;
    }
    if (!signupCode.trim()) {
      setError('Please enter your signup code');
      return;
    }

    try {
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();
      const trimmedEmail = email.trim();
      const trimmedSignupCode = signupCode.trim();

      await auth.register(
        trimmedFirstName,
        trimmedLastName,
        password,
        trimmedEmail,
        phoneNumber.trim(),
        trimmedSignupCode
      );

      try {
        const loginResponse = await auth.login(trimmedEmail, password);
        if (loginResponse.data && loginResponse.data.token) {
          const { token, user } = loginResponse.data;
          await login(
            token,
            normalizeAuthenticatedUser(user),
            {
              method: 'registration_auto_login',
              recoveryConfirmed: loginResponse.data.auth_recovery_performed === true,
              recoverySnapshot: loginResponse.authRecoverySnapshot,
            }
          );
          navigate('/home');
        } else {
          navigate('/login');
        }
      } catch (loginErr) {
        console.error('Auto-login error:', loginErr);
        setError(
          isAuthRevocationSafetyError(loginErr)
            ? loginErr.message
            : 'Your account was created, but we could not finish signing you in. Please sign in to continue.',
        );
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      const emailMessage = err.response?.data?.email?.[0];
      if (emailMessage) {
        setEmailError(emailMessage);
        return;
      }
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    }
  };

  const handleGoogleSignup = async () => {
    if (nativeGoogleAbort.current) return;
    if (!agreedToTerms) {
      setError('You must agree to the Terms and Conditions');
      return;
    }
    if (!signupCode.trim()) {
      setError('Please enter your signup code');
      return;
    }

    setError(null);
    setEmailError(null);

    if (isNativeMobile()) {
      const controller = new AbortController();
      nativeGoogleAbort.current = controller;
      setNativeGooglePending(true);
      try {
        const response = await signInWithNativeGoogle(controller.signal, signupCode.trim());
        if (controller.signal.aborted) return;
        await login(response.data.token, normalizeAuthenticatedUser(response.data.user), {
          method: 'google_oauth',
          recoveryConfirmed: response.data.auth_recovery_performed === true,
          recoverySnapshot: response.authRecoverySnapshot,
        });
        if (!controller.signal.aborted) navigate('/home');
      } catch (failure) {
        if (!controller.signal.aborted) {
          const detail = (failure as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : failure instanceof Error ? failure.message : 'Google signup failed. Please try again.');
        }
      } finally {
        if (nativeGoogleAbort.current === controller) {
          nativeGoogleAbort.current = null;
          setNativeGooglePending(false);
        }
      }
      return;
    }

    try {
      await auth.waitForPendingLogout();
      const params = new URLSearchParams({
        purpose: 'signup',
        signup_code: signupCode.trim(),
      });
      if (auth.requiresLogoutRecovery()) {
        params.set('recover_ambiguous_logout', '1');
      }
      registerNavigation.redirect(
        `${API_BASE_URL}/api/auth/google/initiate/?${params.toString()}`,
      );
    } catch (googleSignupError) {
      console.error('Failed to initiate Google signup:', googleSignupError);
      setError(
        isAuthRevocationSafetyError(googleSignupError)
          ? googleSignupError.message
          : 'Failed to start Google signup. Please try again.',
      );
    }
  };

  return (
    <main className="min-h-screen w-full flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
      <Card>
        <div className="flex justify-center">
          <Icon name="logo" size={64} />
        </div>
        <CardTitle title="Create your account" subtitle="Welcome to A Wise Friend" centered={true}/>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form className="space-y-4 w-full" onSubmit={handleSubmit} noValidate>
            <div className="space-y-2">
              <label htmlFor="signupCode" className="text-sm font-medium text-muted-foreground">
                Signup Code
              </label>
              <Input
                type="text"
                id="signupCode"
                value={signupCode}
                onChange={(e) => setSignupCode(e.target.value)}
                required
                autoComplete="off"
                size="sm"
                placeholder="Enter your signup code"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="firstName" className="text-sm font-medium text-muted-foreground">
                First Name
              </label>
              <Input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                size="sm"
                placeholder="First Name"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="lastName" className="text-sm font-medium text-muted-foreground">
                Last Name
              </label>
              <Input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
                size="sm"
                placeholder="Last Name"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-muted-foreground">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError(null);
                }}
                required
                autoComplete="email"
                size="sm"
                placeholder="name@example.com"
              />
              {emailError && (
                <p className="mt-1 text-xs font-medium text-destructive">{emailError}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="phoneNumber" className="text-sm font-medium text-muted-foreground">
                Phone <span className="text-xs text-muted-foreground">(optional)</span>
              </label>
              <Input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                autoComplete="tel"
                size="sm"
                placeholder="(555) 555-5555"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-muted-foreground">
                Password
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                size="sm"
                placeholder="Password"
                containerClassName="relative"
                className="pr-20"
              />
              {passwordValidationMessage && (
                <p className="mt-1 text-xs font-medium text-destructive">{passwordValidationMessage}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium text-muted-foreground">
                Confirm Password
              </label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                size="sm"
                placeholder="Password"
                containerClassName="relative"
                className="pr-20"
              />
            </div>

            <p className="py-3 text-center text-xs uppercase tracking-[0.3em] text-muted-foreground">or</p>
            <GoogleButton
              signUp
              onClick={handleGoogleSignup}
              disabled={nativeGooglePending}
              isLoading={nativeGooglePending}
            />
            {nativeGooglePending && <>
              <p role="status" className="text-center text-sm">Complete Google signup in your browser, then return to WiseFriend.</p>
              <Button type="button" variant="outline" onClick={() => nativeGoogleAbort.current?.abort()}>Cancel Google signup</Button>
            </>}
            <Separator />
            <TermsCheckbox checked={agreedToTerms} onChange={setAgreedToTerms} />

            <Button type="submit" disabled={isSubmitDisabled}>
              Create Account
            </Button>
          </form>

          <p className="type-body-s text-foundation-base-darkbase-dark-500 text-[0.6875rem] text-center">
            Already have have an account?{" "}
            <ButtonLink to="/login" action="sign_in">Sign In</ButtonLink>
          </p>
        </CardContent>
      </Card>
    </main>
  );
};

export default Register;
